/**
 * Centralized Tab Security Configuration and Validation
 * This module handles all tab access control and permission validation
 */

// Tab configuration with role-based access control
export const TAB_CONFIG = {
  RIDERS: {
    index: 0,
    name: 'riders',
    allowedRoles: ['admin', 'fleetadmin', 'callcentersupervisor', 'callcenteragent', 'finance'],
    public: false
  },
  DRIVERS: {
    index: 1,
    name: 'drivers',
    allowedRoles: ['admin', 'fleetadmin', 'callcentersupervisor', 'callcenteragent', 'finance'],
    public: false
  },
  FLEET_ADMINS: {
    index: 2,
    name: 'fleetadmins',
    allowedRoles: ['admin'],
    public: false
  },
  INTERNAL_STAFF: {
    index: 3,
    name: 'internal_staff',
    allowedRoles: ['admin'],
    public: false
  },
  CREATE_ADMIN: {
    index: 4,
    name: 'alladmin',
    allowedRoles: ['admin'],
    public: false
  }
};

/**
 * Get accessible tabs for a user based on their role
 * @param {string} userRole - The user's role
 * @returns {Array} Array of accessible tab configurations
 */
export const getAccessibleTabs = (userRole) => {
  if (!userRole) return [];
  
  return Object.values(TAB_CONFIG).filter(tab => {
    return tab.public || (tab.allowedRoles && tab.allowedRoles.includes(userRole));
  });
};

/**
 * Validate if a user can access a specific tab index
 * @param {number} tabIndex - The tab index to validate
 * @param {string} userRole - The user's role
 * @returns {boolean} True if user can access the tab
 */
export const canAccessTab = (tabIndex, userRole) => {
  if (!userRole || tabIndex === null || tabIndex === undefined) return false;
  
  const tab = Object.values(TAB_CONFIG).find(t => t.index === tabIndex);
  if (!tab) return false;
  
  return tab.public || (tab.allowedRoles && tab.allowedRoles.includes(userRole));
};

/**
 * Get the default tab index for a user based on their role
 * @param {string} userRole - The user's role
 * @returns {number} Default tab index
 */
export const getDefaultTabIndex = (userRole) => {
  const accessibleTabs = getAccessibleTabs(userRole);
  return accessibleTabs.length > 0 ? accessibleTabs[0].index : 0;
};

/**
 * Validate and sanitize tab index from URL parameter
 * @param {string|number} urlParam - The URL parameter value
 * @param {string} userRole - The user's role
 * @returns {number|null} Validated tab index or null if invalid
 */
export const validateTabIndex = (urlParam, userRole) => {
  // First, ensure the parameter is a valid number
  const tabIndex = parseInt(urlParam, 10);
  
  // Check if it's a valid number
  if (isNaN(tabIndex) || tabIndex < 0) {
    return null;
  }
  
  // Check if the tab exists
  const tabExists = Object.values(TAB_CONFIG).some(tab => tab.index === tabIndex);
  if (!tabExists) {
    return null;
  }
  
  // Check if user has permission to access this tab
  if (!canAccessTab(tabIndex, userRole)) {
    console.warn(`Security: User with role '${userRole}' attempted to access restricted tab index ${tabIndex}`);
    return null;
  }
  
  return tabIndex;
};

/**
 * Get visible tabs configuration for rendering
 * @param {string} userRole - The user's role
 * @returns {Array} Array of tab configurations to render
 */
export const getVisibleTabs = (userRole) => {
  return getAccessibleTabs(userRole).sort((a, b) => a.index - b.index);
};

/**
 * Security audit log for tab access attempts
 * @param {string} userRole - The user's role
 * @param {number} attemptedIndex - The tab index user tried to access
 * @param {boolean} granted - Whether access was granted
 */
export const logTabAccess = (userRole, attemptedIndex, granted) => {
  const timestamp = new Date().toISOString();
  const logEntry = {
    timestamp,
    userRole,
    attemptedIndex,
    granted,
    tabName: Object.values(TAB_CONFIG).find(t => t.index === attemptedIndex)?.name || 'unknown'
  };
  
  /*if (!granted) {
    console.error('Tab Access Denied:', logEntry);
    // In production, you might want to send this to a security monitoring service
  } else {
    console.log('Tab Access Granted:', logEntry);
  }*/
  
  return logEntry;
};

/**
 * Check if a role has any admin privileges
 * @param {string} userRole - The user's role
 * @returns {boolean} True if role has admin privileges
 */
export const hasAdminPrivileges = (userRole) => {
  return userRole === 'admin';
};

/**
 * Check if a role has supervisor privileges
 * @param {string} userRole - The user's role
 * @returns {boolean} True if role has supervisor privileges
 */
export const hasSupervisorPrivileges = (userRole) => {
  return ['admin', 'fleetadmin', 'callcentersupervisor'].includes(userRole);
};

/**
 * Get the maximum tab index a user can access
 * @param {string} userRole - The user's role
 * @returns {number} Maximum accessible tab index
 */
export const getMaxAccessibleTabIndex = (userRole) => {
  const accessibleTabs = getAccessibleTabs(userRole);
  if (accessibleTabs.length === 0) return -1;
  
  return Math.max(...accessibleTabs.map(tab => tab.index));
};

/**
 * Validate tab transition (for preventing direct URL manipulation)
 * @param {number} fromIndex - Current tab index
 * @param {number} toIndex - Target tab index
 * @param {string} userRole - The user's role
 * @returns {boolean} True if transition is allowed
 */
export const canTransitionToTab = (fromIndex, toIndex, userRole) => {
  // Check if both tabs are accessible
  if (!canAccessTab(fromIndex, userRole) || !canAccessTab(toIndex, userRole)) {
    return false;
  }
  
  // Additional business logic for tab transitions can be added here
  // For example, preventing certain tab jumps without completing actions
  
  return true;
};

/**
 * Get security warnings for a tab
 * @param {number} tabIndex - The tab index
 * @returns {Array} Array of warning messages
 */
export const getTabSecurityWarnings = (tabIndex) => {
  const warnings = [];
  const tab = Object.values(TAB_CONFIG).find(t => t.index === tabIndex);
  
  if (!tab) {
    warnings.push('Invalid tab index');
    return warnings;
  }
  
  if (tab.allowedRoles.includes('admin') && tab.allowedRoles.length === 1) {
    warnings.push('This tab contains highly sensitive admin-only data');
  }
  
  if (tab.name === 'internal_staff' || tab.name === 'alladmin') {
    warnings.push('This tab allows user role modifications');
  }
  
  return warnings;
};
